import { useEffect, useState } from 'react';
import { X, Phone, Tag, Pencil, Check, Trash2, Camera, Link2, Link2Off, Loader2, Smartphone, ShoppingBag } from 'lucide-react';
import { formatDataSP, formatMoeda } from '@fiodecobre/shared';
import { ModalConvidarApp } from '../modais/ModalConvidarApp';
import { Avatar } from '../../../components/Avatar';
import { BadgeEtiqueta } from './BadgeEtiqueta';
import { SeletorEtiquetas } from './SeletorEtiquetas';
import { ModalUploadFotoContato } from '../modais/ModalUploadFotoContato';
import { ModalEtiquetas } from '../modais/ModalEtiquetas';
import { useAuth } from '../../../lib/auth';
import { useToast } from '../../../components/Toast';
import { useDialog } from '../../../components/DialogProvider';
import { atendimentoApi } from '../lib/api';
import { useEtiquetas } from '../lib/etiquetas-context';
import type { Contato } from '../lib/tipos';

interface Props {
  contato: Contato;
  /** Conversa aberta — por onde o convite do app e' enviado. */
  conversaId?: number;
  onFechar: () => void;
  onAtualizar: (c: Contato) => void;
  onApagar: (id: number) => void;
}

export function PainelContato({ contato, conversaId, onFechar, onAtualizar, onApagar }: Props) {
  const { usuario } = useAuth();
  const ehAdmin = usuario?.perfil === 'admin' || usuario?.perfil === 'gerente';
  // `visiveis`, nao `todas`: a etiqueta marcada como Ocultar sai da ficha e do seletor.
  // ⚠️ Isso e' SO' exibicao — `etqIds` guarda os ids que o contato tem (ocultos inclusive),
  // entao marcar qualquer etiqueta aqui nao apaga o carimbo do rodizio.
  const { visiveis: todasEtiquetas } = useEtiquetas();
  const toast = useToast();
  const dialog = useDialog();

  const [etqIds, setEtqIds] = useState<number[]>(contato.etiquetas?.map((e) => e.id) ?? []);
  // ⛔ **A promessa do comentario acima so' vale se `etqIds` estiver CHEIO** — e o `useState` le a
  // prop UMA VEZ. Conversa aberta por link direto (`/atendimento?telefone=...`, que e' o botao de
  // WhatsApp da Gestao de Vendas e o "conversar com numero") vem do `POST /api/conversas`, que
  // devolve o contato CRU, **sem `etiquetas`** — a lista e o `GET /:id` trazem, ele nao. Ai a
  // ficha dizia "Nenhuma etiqueta" para quem tinha duas, e marcar uma **apagava as outras**,
  // porque o `PUT /contatos/:id/etiquetas` faz DELETE de tudo e reinsere o que recebe.
  // Medido em prod (09/09/2026): contato 35608 com `SOLAR` + `cristina` (a do rodizio) abrindo
  // por link direto com o seletor inteiro em branco. ⚠️ Nao basta preencher: enquanto nao souber
  // o conjunto real, o seletor fica DESABILITADO — chute aqui apaga vinculo de verdade.
  const [etqCarregado, setEtqCarregado] = useState(!!contato.etiquetas);
  const [pickerAberto, setPickerAberto] = useState(false);
  const [editando, setEditando] = useState(false);
  const [nome, setNome] = useState(contato.nome ?? '');
  const [sobrenome, setSobrenome] = useState(contato.sobrenome ?? '');
  const [telefone, setTelefone] = useState(contato.telefone ?? '');
  const [salvando, setSalvando] = useState(false);
  // Recusa da troca de numero (sem WhatsApp, ja e' de outro contato...). Fica na tela ate a
  // proxima tentativa — toast de 4s nao se rele.
  const [erroTelefone, setErroTelefone] = useState<string | null>(null);
  const [excluindo, setExcluindo] = useState(false);
  const [desvinculando, setDesvinculando] = useState(false);
  const [fotoAmpliada, setFotoAmpliada] = useState(false);
  const [trocarFotoAberto, setTrocarFotoAberto] = useState(false);
  const [convidarAberto, setConvidarAberto] = useState(false);
  const [gerenciarEtqAberto, setGerenciarEtqAberto] = useState(false);
  /**
   * ⛔ A ultima compra NAO vem no `contato` que este painel recebe: a prop chega da CONVERSA (a
   * lista), e o campo s'o existe no `GET /api/contatos/:id`. Sem esta busca a ficha diria "sem
   * compra registrada" pra TODO MUNDO — e nada denunciaria, porque o tipo e' opcional, o
   * typecheck passa e a tela desenha certinho o estado vazio.
   * `undefined` = ainda buscando, `null` = buscou e nao ha. Sao coisas diferentes na tela.
   */
  const [ultimaCompra, setUltimaCompra] = useState<Contato['ultima_compra'] | undefined>(undefined);

  // ✅ O conserto das etiquetas nao custa chamada nova: esta rota **ja devolve `etiquetas`**
  // (cross-inbox por JID, `GET /api/contatos/:id`) e o painel ja a chamava por causa da ultima
  // compra — o dado vinha e era jogado fora. ⚠️ No `catch` o `etqCarregado` fica como estava:
  // seletor travado quando nao houve leitura nenhuma, liberado quando a prop ja trouxe o conjunto.
  useEffect(() => {
    let cancelado = false;
    setUltimaCompra(undefined);
    atendimentoApi.obterContato(contato.id)
      .then((c) => {
        if (cancelado) return;
        setUltimaCompra(c.ultima_compra ?? null);
        setEtqIds((c.etiquetas ?? []).map((e) => e.id));
        setEtqCarregado(true);
      })
      .catch(() => { if (!cancelado) setUltimaCompra(null); });
    return () => { cancelado = true; };
  }, [contato.id]);

  useEffect(() => {
    setEtqIds(contato.etiquetas?.map((e) => e.id) ?? []);
    setEtqCarregado(!!contato.etiquetas);
    setNome(contato.nome ?? '');
    setSobrenome(contato.sobrenome ?? '');
    setTelefone(contato.telefone ?? '');
    setEditando(false);
  }, [contato.id]);

  async function alternarEtiqueta(id: number) {
    const novo = etqIds.includes(id) ? etqIds.filter((x) => x !== id) : [...etqIds, id];
    try {
      await atendimentoApi.atualizarEtiquetasContato(contato.id, novo);
      setEtqIds(novo);
      toast.sucesso('Etiquetas atualizadas');
    } catch {
      toast.erro('Erro ao atualizar etiquetas');
    }
  }

  async function salvar() {
    if (!nome.trim()) return;
    setSalvando(true);
    setErroTelefone(null);
    // ⛔ Numero trocado passa pela conferencia com o WhatsApp, que grava o telefone E o endereco de
    // envio (`jid`) — decisao dele em 24/09/2026. A conferencia mora no SERVIDOR (o PUT a aplica):
    // na tela ela deixava a aba velha furar, que foi o caso do Ricardo. Aqui so' se mostra a recusa.
    try {
      const atualizado = await atendimentoApi.atualizarContato(contato.id, {
        nome: nome.trim(),
        sobrenome: sobrenome.trim() || null,
        telefone: telefone.trim() || null,
      });
      setEditando(false);
      toast.sucesso('Contato atualizado');
      onAtualizar(atualizado);
    } catch (e) {
      setErroTelefone((e as Error).message || 'Erro ao salvar');
    } finally {
      setSalvando(false);
    }
  }

  async function excluir() {
    const ok = await dialog.confirmar(
      `Excluir permanentemente "${contato.nome || contato.telefone}"?\n\nIsso apaga o contato, todas as conversas e mensagens. Não tem como desfazer.`,
      { titulo: 'Excluir contato?', textoConfirmar: 'Excluir', perigoso: true },
    );
    if (!ok) return;
    setExcluindo(true);
    try {
      await atendimentoApi.excluirContato(contato.id);
      toast.sucesso('Contato excluído');
      onApagar(contato.id);
    } catch (e) {
      // Desde 24/09/2026 o servidor diz A QUE o contato esta preso (orcamento, App, campanha...)
      // em vez de estourar na FK — mostrar o motivo, que "erro" sozinho nao explica nada.
      await dialog.alerta((e as Error).message || 'Erro ao excluir', { titulo: 'Não excluiu' });
    } finally {
      setExcluindo(false);
    }
  }

  async function desvincular() {
    const ok = await dialog.confirmar(
      `Desvincular este contato do cliente ${contato.cliente_nome ?? ''}? O contato continua aqui, so deixa de apontar pra esse cadastro.`,
      { titulo: 'Desvincular cadastro?', textoConfirmar: 'Desvincular', perigoso: true },
    );
    if (!ok) return;
    setDesvinculando(true);
    try {
      const atualizado = await atendimentoApi.atualizarContato(contato.id, { cliente_id: null });
      toast.sucesso('Cadastro desvinculado');
      onAtualizar(atualizado);
    } catch {
      toast.erro('Erro ao desvincular');
    } finally {
      setDesvinculando(false);
    }
  }

  const nomeCompleto = [contato.nome, contato.sobrenome].filter(Boolean).join(' ') || contato.push_name || contato.telefone || 'Desconhecido';
  const ehTelefoneNoNome = !contato.nome || contato.nome === contato.telefone;
  const etqsAtuais = todasEtiquetas.filter((e) => etqIds.includes(e.id));

  return (
    <div className="fixed inset-0 z-50 md:relative md:inset-auto md:z-auto w-full md:w-72 border-l bg-white flex flex-col flex-shrink-0">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <span className="font-medium text-sm">Detalhes do Contato</span>
        <button onClick={onFechar} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="flex flex-col items-center">
          <div className="relative">
            <div className={contato.foto_url ? 'cursor-pointer' : ''} onClick={() => contato.foto_url && setFotoAmpliada(true)}>
              <Avatar nome={contato.nome || contato.push_name || ''} fotoUrl={contato.foto_url} tamanho={64} />
            </div>
            {editando && ehAdmin && (
              <button
                onClick={() => setTrocarFotoAberto(true)}
                className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-blue-600 text-white shadow flex items-center justify-center hover:bg-blue-700"
                title="Trocar foto"
              >
                <Camera size={13} />
              </button>
            )}
          </div>
          {!editando ? (
            <>
              <h3 className="font-medium text-gray-800 mt-2 text-center">{nomeCompleto}</h3>
              {contato.push_name && contato.nome !== contato.push_name && (
                <p className="text-xs text-gray-400">WhatsApp: {contato.push_name}</p>
              )}
              {ehAdmin && (
                <button onClick={() => setEditando(true)} className="mt-1 text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
                  <Pencil size={11} /> {ehTelefoneNoNome ? 'Cadastrar dados' : 'Editar contato'}
                </button>
              )}
              {/* Convite pro app: so faz sentido com telefone e fora de grupo (link
                  de identidade num grupo e o primeiro que tocar leva o cadastro).
                  Sem `conversaId` o modal nao teria por onde enviar. */}
              {conversaId != null && contato.telefone && !contato.grupo && (
                <button onClick={() => setConvidarAberto(true)} className="mt-1 text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
                  <Smartphone size={11} /> Convidar para o aplicativo
                </button>
              )}
            </>
          ) : (
            <div className="w-full mt-3 space-y-2">
              {/* ⚠️ `no-upper` e' OBRIGATORIO aqui, nao enfeite. Nome de contato e' caixa livre
                  por decisao do Ruan (14/08): a lista e' cheia de nome vindo do WhatsApp, que ja
                  chega em caixa natural, e um nome forcado em maiusculo destoa dos vizinhos.
                  Mas o `text-transform` global do `styles.css` e' VISUAL: sem `no-upper` o campo
                  mostraria maiusculo e gravaria o que foi teclado — que e' exatamente o defeito
                  relatado neste dia. Caixa livre so e' honesta se a tela mostrar o que sera gravado.
                  ⛔ Nao usar `<UpperInput/>` nestes dois: ele forca o VALOR. */}
              <div>
                <label className="text-xs text-gray-500 block mb-0.5">Nome</label>
                <input value={nome} onChange={(e) => setNome(e.target.value)} className="no-upper w-full px-2.5 py-1.5 text-sm border rounded-lg outline-none focus:ring-2 focus:ring-blue-400" style={{ fontSize: '16px' }} placeholder="Nome" autoFocus />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-0.5">Sobrenome</label>
                <input value={sobrenome} onChange={(e) => setSobrenome(e.target.value)} className="no-upper w-full px-2.5 py-1.5 text-sm border rounded-lg outline-none focus:ring-2 focus:ring-blue-400" style={{ fontSize: '16px' }} placeholder="Sobrenome" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-0.5">Telefone</label>
                <input type="tel" value={telefone} onChange={(e) => { setTelefone(e.target.value); setErroTelefone(null); }} className="w-full px-2.5 py-1.5 text-sm border rounded-lg outline-none focus:ring-2 focus:ring-blue-400" style={{ fontSize: '16px' }} placeholder="DDD + número" />
                {erroTelefone ? (
                  <div className="mt-1 px-1.5 py-1 rounded text-xs font-bold" style={{ backgroundColor: '#fde68a', color: '#000' }}>
                    {erroTelefone}
                  </div>
                ) : !contato.grupo && (
                  <div className="mt-0.5 text-[11px] text-gray-500">
                    Ao trocar o número, ele é conferido no WhatsApp antes de salvar.
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={salvar} disabled={salvando || !nome.trim()} className="flex-1 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-1">
                  <Check size={14} /> {salvando ? 'Salvando...' : 'Salvar'}
                </button>
                <button onClick={() => { setEditando(false); setErroTelefone(null); setNome(contato.nome ?? ''); setSobrenome(contato.sobrenome ?? ''); setTelefone(contato.telefone ?? ''); }} className="px-3 py-1.5 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">
                  Cancelar
                </button>
              </div>
              {ehAdmin && (
                <button onClick={excluir} disabled={excluindo} className="w-full mt-2 py-1.5 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 flex items-center justify-center gap-1">
                  <Trash2 size={14} /> {excluindo ? 'Excluindo...' : 'Excluir contato'}
                </button>
              )}
            </div>
          )}
        </div>

        {!editando && contato.telefone && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Phone size={14} className="text-gray-400" />
            <span>{contato.telefone}</span>
          </div>
        )}

        {!editando && contato.cliente_id && (
          <div className="rounded-lg p-2.5" style={{ backgroundColor: '#fde68a', border: '1px solid #fbbf24' }}>
            <div className="flex items-center gap-1.5 text-xs font-medium text-black/70 mb-0.5">
              <Link2 size={12} /> Cliente vinculado
            </div>
            <div className="text-sm font-medium text-black">{contato.cliente_nome ?? `Cliente #${contato.cliente_id}`}</div>
            {contato.cliente_codigo && <div className="text-xs text-black/70">Codigo: {contato.cliente_codigo}</div>}
            {ehAdmin && (
              <button
                onClick={desvincular}
                disabled={desvinculando}
                className="mt-2 w-full py-1.5 text-sm font-medium rounded-lg border flex items-center justify-center gap-1.5 disabled:opacity-50"
                style={{ borderColor: '#fca5a5', color: '#991b1b', backgroundColor: 'rgba(254,226,226,0.6)' }}
              >
                {desvinculando ? <Loader2 size={14} className="animate-spin" /> : <Link2Off size={14} />}
                {desvinculando ? 'Desvinculando...' : 'Desvincular cadastro'}
              </button>
            )}
          </div>
        )}

        {/* ÚLTIMA COMPRA — pedido dele em 08/09/2026: o dado nasceu como coluna da Gestao de
            Vendas e ele mudou o lugar pra ca, *"dessa forma onde eu puxar o dado do contato, vou
            conseguir visualizar esta informacao"*. Ficou melhor: aparece na hora da CONVERSA, que
            e' quando serve pra decidir o que dizer.
            ⚠️ O vazio e' EXPLICITO ("sem compra registrada"), nao um sumico: quem le precisa saber
            a diferenca entre "nunca comprou" e "a tela nao mostra isso". Hoje s'o 113 dos 1.133
            contatos tem compra ligada — sobe sozinho conforme a captura de nome/telefone pegar. */}
        {!editando && (
          <div className="rounded-lg p-2.5 border" style={{ borderColor: 'var(--border, rgba(0,0,0,0.12))' }}>
            <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 mb-0.5">
              <ShoppingBag size={12} /> Ultima compra
            </div>
            {ultimaCompra === undefined ? (
              <div className="text-sm text-gray-400">procurando...</div>
            ) : ultimaCompra ? (
              <>
                <div className="text-sm font-medium text-gray-800">
                  {formatDataSP(ultimaCompra.quando)} · {formatMoeda(ultimaCompra.total)}
                </div>
                <div className="text-[11px] text-gray-500">
                  {ultimaCompra.numero}
                  {ultimaCompra.via === 'telefone' ? ' · achada pelo WhatsApp' : ' · pelo cadastro do cliente'}
                </div>
              </>
            ) : (
              <div className="text-sm text-gray-400">sem compra registrada</div>
            )}
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 flex items-center gap-1">
              <Tag size={14} /> Etiquetas
            </span>
            {ehAdmin && (
              <span className="flex items-center gap-2">
                <button onClick={() => setPickerAberto(!pickerAberto)} className="text-xs text-blue-600 hover:text-blue-800">
                  {pickerAberto ? 'Fechar' : 'Editar'}
                </button>
                {/* Reusa o modal de Configuracoes — incluir/renomear/excluir moram numa peca so. */}
                <button onClick={() => setGerenciarEtqAberto(true)} className="text-xs text-blue-600 hover:text-blue-800">
                  Gerenciar
                </button>
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-1">
            {etqsAtuais.length === 0 && <span className="text-xs text-gray-400">Nenhuma etiqueta</span>}
            {etqsAtuais.map((e) => <BadgeEtiqueta key={e.id} nome={e.nome} cor={e.cor} apelido={e.apelido} />)}
          </div>
          {/* ⚠️ **Grade de chips, a mesma peca da aba "Contatos do dia"** ([DA-694], pedido dele no
              mesmo dia: *"Tem como atualizar agora o painel de contatos?"*). ⛔ **Sem
              `avisoEmDuasColunas` aqui**: o painel e' `w-72` (252px, ~202 uteis com o `p-4` e a
              moldura), entao a grade tem UMA coluna e o `span 2` estouraria a lateral — o aviso
              do `nao_cliente` quebra pra segunda linha do proprio chip. ⛔ `colunaMin` menor que
              o da Gestao pelo mesmo motivo; em tela pequena o painel vira `w-full` e a grade
              acompanha sozinha. */}
          {ehAdmin && pickerAberto && (
            <div className="mt-2 border rounded-lg p-2 max-h-48 overflow-y-auto">
              {/* ⚠️ A trava DIZ por que ela existe — botao apagado e mudo e' beco sem saida. */}
              {!etqCarregado && (
                <div className="text-[11px] mb-1.5" style={{ color: 'var(--text-muted)' }}>
                  Lendo as etiquetas deste contato... marcar antes disso apagaria as que ele ja tem.
                </div>
              )}
              <SeletorEtiquetas
                etiquetas={todasEtiquetas}
                marcadas={etqIds}
                onAlternar={(id) => alternarEtiqueta(id)}
                desabilitado={!etqCarregado}
                colunaMin={150}
              />
            </div>
          )}
        </div>
      </div>

      {fotoAmpliada && contato.foto_url && (
        <div className="fixed inset-0 z-[200] bg-black/70 flex items-center justify-center" onClick={() => setFotoAmpliada(false)}>
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <img src={contato.foto_url} alt={contato.nome} className="max-w-[90vw] max-h-[80vh] rounded-xl shadow-2xl" />
            <button onClick={() => setFotoAmpliada(false)} className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full shadow flex items-center justify-center text-gray-600 hover:text-gray-900">
              <X size={18} />
            </button>
          </div>
        </div>
      )}

      <ModalUploadFotoContato
        aberto={trocarFotoAberto}
        contato={contato}
        onFechar={(atualizado) => {
          setTrocarFotoAberto(false);
          if (atualizado) onAtualizar(atualizado);
        }}
      />

      {conversaId != null && (
        <ModalConvidarApp
          aberto={convidarAberto}
          contatoId={contato.id}
          conversaId={conversaId}
          onFechar={() => setConvidarAberto(false)}
        />
      )}

      {ehAdmin && (
        <ModalEtiquetas aberto={gerenciarEtqAberto} onFechar={() => setGerenciarEtqAberto(false)} />
      )}
    </div>
  );
}
